import { WarningOutlined } from '@ant-design/icons';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppInput from '@nxsys/components/AppInput';
import {
  getLatestHistoryLeaverApi,
  reinstatementEmployeeApi,
} from '@nxsys/services/api/subcontractor';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { LatestHistoryLeaverData } from '@nxsys/types/api/subcontractor';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { showMessage } from '@nxsys/utils/message';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { taxCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import { Alert, Form, Radio, Table } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type ReinstatementModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data: EmployeeRecordType;
  onSuccess: () => void;
};

type FormValues = {
  taxCode: string;
  taxBasis: string;
  p45GrossToDate: number;
  p45TaxToDate: number;
  dateOfJoining: moment.Moment | null;
};

const ReinstatementModal: React.FC<ReinstatementModalProps> = ({
  isOpen,
  toggle,
  data,
  onSuccess,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const [latestData, setLatestData] = useState<LatestHistoryLeaverData | null>(
    null,
  );
  const [palBlockError, setPalBlockError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const formValues = Form.useWatch([], form);

  const handleGetData = async () => {
    if (!data?.id) return;
    setIsFetching(true);
    try {
      const res = await getLatestHistoryLeaverApi(data.id);
      setLatestData(res?.data ?? null);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isOpen && data?.id) {
      setPalBlockError(null);
      handleGetData();
    }
  }, [isOpen, data?.id]);

  useEffect(() => {
    if (latestData) {
      const defaultDateOfJoining = data.dateLeft
        ? moment(data.dateLeft).add(1, 'day')
        : moment();

      form.setFieldsValue({
        taxCode:
          data.companyPayment?.autoEnrolSettings?.taxCode ??
          latestData.taxCode ??
          '1257L',
        taxBasis: latestData.taxBasis ?? 'CUMULATIVE',
        p45GrossToDate: Number(latestData.p45GrossToDate?.toFixed(2)) || 0,
        p45TaxToDate: Number(latestData.p45TaxToDate?.toFixed(2)) || 0,
        dateOfJoining: defaultDateOfJoining,
      });
    }
  }, [latestData]);

  const handleSubmit = async () => {
    try {
      // Run the Form.Item rules (e.g. taxCodeValidationRule) before submitting.
      // This modal submits via a modal-footer button (getFieldsValue), not the
      // Form's onFinish, so without this the rules never fire and an invalid
      // tax code would be sent to the API. The tax-code field is `noStyle`, so
      // surface the rule message as a toast (the inline message is suppressed).
      await form.validateFields();
    } catch (err: any) {
      const firstError = err?.errorFields?.[0]?.errors?.[0];
      if (firstError) {
        showMessage({ type: 'error', content: firstError });
      }
      return;
    }

    const values = form.getFieldsValue(true);
    if (!values.dateOfJoining) {
      showMessage({ type: 'error', content: 'Date of joining is required.' });
      return;
    }

    if (
      data.dateLeft &&
      moment(values.dateOfJoining).isBefore(moment(data.dateLeft))
    ) {
      showMessage({
        type: 'error',
        content: 'The date of joining must be after the date left.',
      });
      return;
    }

    setIsLoading(true);
    setPalBlockError(null);
    try {
      await reinstatementEmployeeApi(data.id, {
        taxCode: values.taxCode,
        taxBasis: values.taxBasis,
        p45GrossToDate: Number(values.p45GrossToDate) || 0,
        p45TaxToDate: Number(values.p45TaxToDate) || 0,
        dateOfJoining: moment(values.dateOfJoining).toISOString(),
        settingId: data.setting?.id,
      });
      showMessage({ type: 'success', content: 'Reinstated Successfully!' });
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || '';
      if (
        errorMessage.toLowerCase().includes('pending pal') ||
        errorMessage.toLowerCase().includes('payment after leaving')
      ) {
        setPalBlockError(errorMessage);
      } else {
        console.error(error);
        showMessage({
          type: 'error',
          content:
            errorMessage || 'Failed to reinstate employee. Please try again.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tableData = useMemo(() => {
    if (!formValues) return [];
    return [
      {
        key: 'taxCode',
        title: 'Tax Code & Tax Basis',
        previousData: `${latestData?.previousTaxCode || '_'} ${
          latestData?.previousTaxBasis
            ? latestData.previousTaxBasis === 'W1M1'
              ? 'W1M1'
              : 'Cumulative'
            : '_'
        }`,
        type: 'taxCode' as const,
      },
      {
        key: 'grossForNI',
        title: 'Niable Gross to Date',
        previousData: latestData?.grossForNI ?? 0,
        type: 'readonly' as const,
        isNumber: true,
      },
      {
        key: 'grossForTax',
        title: 'Taxable Gross to Date',
        previousData: latestData?.grossForTax ?? 0,
        type: 'readonly' as const,
        isNumber: true,
      },
      {
        key: 'tax',
        title: 'Tax to Date',
        previousData: latestData?.tax ?? 0,
        type: 'readonly' as const,
        isNumber: true,
      },
      {
        key: 'p45GrossToDate',
        title: 'P45 Gross to Date',
        previousData: latestData?.previousP45GrossToDate ?? 0,
        type: 'editable' as const,
        fieldName: 'p45GrossToDate',
        isNumber: true,
      },
      {
        key: 'p45TaxToDate',
        title: 'P45 Tax to Date',
        previousData: latestData?.previousP45TaxToDate ?? 0,
        type: 'editable' as const,
        fieldName: 'p45TaxToDate',
        isNumber: true,
      },
    ];
  }, [latestData, formValues]);

  const columns = [
    {
      title: '',
      width: '30%',
      render: (record: (typeof tableData)[0]) => <b>{record.title}</b>,
    },
    {
      title: 'Previous tax data',
      width: '30%',
      render: (record: (typeof tableData)[0]) =>
        record.isNumber
          ? formatMoney(record.previousData as number, showCurrencyByCountry(payrollCountry))
          : record.previousData,
    },
    {
      title: 'New tax data',
      render: (record: (typeof tableData)[0]) => {
        if (record.type === 'readonly') {
          return formatMoney(0, showCurrencyByCountry(payrollCountry));
        }
        if (record.type === 'taxCode') {
          return (
            <div className="flex items-center gap-x-4">
              <Form.Item
                name="taxCode"
                rules={[taxCodeValidationRule()]}
                noStyle
              >
                <AppInput
                  upperCaseAll
                  style={{ width: 120 }}
                  placeholder="Tax Code"
                />
              </Form.Item>
              <Form.Item name="taxBasis" noStyle>
                <Radio.Group>
                  <Radio value="CUMULATIVE">Cumulative</Radio>
                  <Radio value="W1M1">W1M1</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          );
        }
        return (
          <Form.Item name={record.fieldName} noStyle>
            <AppCurrencyInput />
          </Form.Item>
        );
      },
    },
  ];

  const minDate = data.dateLeft
    ? moment(data.dateLeft).add(1, 'day')
    : moment();

  return (
    <AppModalContainer
      title="Reinstate Employee"
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: isLoading ? 'Processing...' : 'Confirm',
          buttonType: 'primary',
          onClick: handleSubmit,
          loading: isLoading,
          disabled: isLoading || !!palBlockError,
        },
      ]}
      minHeight="0px"
      width={1200}
    >
      <AppFormContainer
        form={form}
        layout="vertical"
        initialValues={{ taxBasis: 'CUMULATIVE' }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {palBlockError && (
          <Alert
            message="Reinstatement Blocked"
            description={
              <>
                <p>{palBlockError}</p>
                <p className="mb-0">
                  Please process all pending Payment After Leaving (PAL)
                  timesheets before reinstating this employee.
                </p>
              </>
            }
            type="error"
            showIcon
            icon={<WarningOutlined />}
            className="mb-4"
          />
        )}
        <div className="mb-4">
          <span>
            No employment found for the employee within the current tax year.
            The employee will still be restarted with their most recent
            employment, however, no tax information will be carried forward.
            Below we have proposed new tax information, you can choose to accept
            this proposal or confirm that you will manually verify their
            information.
          </span>
        </div>
        <div className="mb-4" style={{ maxWidth: 300 }}>
          <Form.Item
            label="Date Of Joining"
            name="dateOfJoining"
            rules={[{ required: true, message: 'Please select' }]}
          >
            <AppDatePicker
              placeholder="Select date"
              format="DD/MM/YYYY"
              disabledDate={(current: moment.Moment) =>
                current && current.isBefore(minDate, 'day')
              }
            />
          </Form.Item>
        </div>
        {!isIreland && (
          <Table
            bordered
            rowKey="key"
            size="middle"
            columns={columns}
            dataSource={tableData}
            pagination={false}
            loading={isFetching}
          />
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default ReinstatementModal;
